/**
 * A line's artwork file for the supplier (doc 65 §3.1): *Copy file link* where the view offers it, otherwise why
 * not. Issuing gives the supplier new links to the line's print-ready files and stops the earlier ones working; the
 * links are shown once (one per version and part, under their white-label names) and copied. They are held in this
 * component's state only: never stored, never put in the URL or the log.
 *
 * On a line sent through the supplier's API the view sends a confirmation (Ben, v1 completion M Q12): the supplier
 * already holds a link, and CIS cannot send it a new one, so the person is asked first. Every sentence is the view's.
 * Used by the placement page and the purchase order page.
 */
import { useState } from "react";
import { Copy, Link2 } from "lucide-react";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  FileLinkConfirmation,
  FileLinkLine,
  SupplierFileLink as IssuedFileLink,
} from "@shared-core/data/order-to-invoice-types";
import { formatShortDateOrDash } from "@shared-core/domain/formatters";
import { orderStepFailure } from "@shared-core/domain/order-to-invoice-presentation";
import { Alert, AlertDescription } from "../../primitives/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../primitives/alert-dialog";
import { Button } from "../../primitives/button";

type FileLinkState =
  | { kind: "idle" }
  | { kind: "confirming" }
  | { kind: "issuing" }
  | {
      kind: "issued";
      links: IssuedFileLink[];
      copied: string[];
      copyFailed: boolean;
    }
  | { kind: "refused"; message: string };

export function SupplierFileLink({
  line,
  supplier,
  unavailableReason = null,
  confirmation = null,
}: {
  line: FileLinkLine;
  supplier: string;
  /** Why the link is not offered ("Not released yet"), where the view says; nothing is shown without either. */
  unavailableReason?: string | null;
  confirmation?: FileLinkConfirmation | null;
}) {
  const adapter = useDataAdapter();
  const capabilities = usePlatformCapabilities();
  const [state, setState] = useState<FileLinkState>({ kind: "idle" });
  if (!line.issue_file_link_url && !unavailableReason) return null;

  const copy = async (text: string, ...names: string[]) => {
    // A failed copy leaves the links on the page: they are shown once, so the person must be able to copy again.
    try {
      await capabilities.copyToClipboard(text);
      setState((current) =>
        current.kind === "issued"
          ? {
              ...current,
              copied: [...current.copied, ...names],
              copyFailed: false,
            }
          : current,
      );
    } catch {
      setState((current) =>
        current.kind === "issued" ? { ...current, copyFailed: true } : current,
      );
    }
  };

  const issue = async () => {
    setState({ kind: "issuing" });
    try {
      const { links } = await adapter.issueFileLink(line);
      setState({ kind: "issued", links, copied: [], copyFailed: false });
      // One file: it is the link to paste, so it is copied at once.
      const [only] = links;
      if (only && links.length === 1) await copy(only.href, fileName(only));
    } catch (err) {
      setState({ kind: "refused", message: orderStepFailure(err).summary });
    }
  };

  return (
    <div className="space-y-2 border-t border-border py-1.5">
      <div className="grid grid-cols-[8rem_minmax(0,1fr)_auto] items-center gap-2">
        <span className="text-xs text-muted-foreground">Artwork link</span>
        <span className="min-w-0 text-sm text-muted-foreground">
          {line.issue_file_link_url
            ? `The print-ready file for ${supplier}, as a link that expires`
            : unavailableReason}
        </span>
        {line.issue_file_link_url && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 px-2"
            disabled={state.kind === "issuing"}
            onClick={() =>
              confirmation ? setState({ kind: "confirming" }) : void issue()
            }
          >
            <Link2 aria-hidden className="size-3.5" />
            Copy file link
          </Button>
        )}
      </div>
      {state.kind === "refused" && (
        <Alert variant="destructive" role="alert">
          <AlertDescription>{state.message}</AlertDescription>
        </Alert>
      )}
      {state.kind === "issued" && (
        <IssuedLinks
          links={state.links}
          copied={state.copied}
          copyFailed={state.copyFailed}
          onCopy={(text, ...names) => void copy(text, ...names)}
        />
      )}
      {confirmation && (
        <AlertDialog
          open={state.kind === "confirming"}
          onOpenChange={(open) => !open && setState({ kind: "idle" })}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{confirmation.title}</AlertDialogTitle>
              <AlertDialogDescription>
                {confirmation.body}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => void issue()}>
                {confirmation.confirm_label}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}

/**
 * A link's label is the name the supplier sees, then (among several) " · version A, front". Only the name is pasted
 * into the supplier's order form; the rest is shown beside it.
 */
function fileName(link: IssuedFileLink): string {
  return link.label.split(" · ")[0]!;
}

function versionAndPart(link: IssuedFileLink): string | null {
  const [, rest] = link.label.split(" · ");
  return rest ?? null;
}

/** The links just issued: shown this once, each with its own Copy, and all of them at once. */
function IssuedLinks({
  links,
  copied,
  copyFailed,
  onCopy,
}: {
  links: IssuedFileLink[];
  copied: string[];
  copyFailed: boolean;
  onCopy: (text: string, ...names: string[]) => void;
}) {
  const expiresAt = links[0]?.expires_at;
  const expires = expiresAt
    ? formatShortDateOrDash(expiresAt.slice(0, 10))
    : "";
  const several = links.length > 1;
  return (
    <div
      className="space-y-1 rounded-md border border-border bg-muted/40 p-3"
      role="status"
      aria-label="File links issued"
    >
      <p className="text-sm">
        {several
          ? `${links.length} links issued, valid until ${expires}.`
          : `Link ${copied.length > 0 ? "copied" : "issued"}, valid until ${expires}.`}{" "}
        <span className="text-muted-foreground">
          They are shown once. Copy file link again to issue new ones; these
          stop working.
        </span>
      </p>
      {copyFailed && (
        <p role="alert" className="text-sm text-red-600">
          The browser would not copy the link to the clipboard. Use Copy beside
          the file to try again.
        </p>
      )}
      <ul className="space-y-1">
        {links.map((file) => (
          <li
            key={file.href}
            className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2"
          >
            <span className="min-w-0 truncate text-sm">
              {fileName(file)}
              {versionAndPart(file) ? (
                <span className="text-muted-foreground">
                  {" "}
                  · {versionAndPart(file)}
                </span>
              ) : null}
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 px-2"
              aria-label={`Copy link for ${fileName(file)}`}
              onClick={() => onCopy(file.href, fileName(file))}
            >
              <Copy aria-hidden className="size-3.5" />
              {copied.includes(fileName(file)) ? "Copied" : "Copy"}
            </Button>
          </li>
        ))}
      </ul>
      {several && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 px-2"
          onClick={() =>
            onCopy(
              links.map((f) => `${fileName(f)}\n${f.href}`).join("\n\n"),
              ...links.map(fileName),
            )
          }
        >
          <Copy aria-hidden className="size-3.5" />
          Copy all links
        </Button>
      )}
    </div>
  );
}
